import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateLead } from '@/api/leads.api';
import { ApiError } from '@/api/errors';
import { FormField } from '@/components/form/FormField';
import { LookupSelect } from '@/components/form/LookupSelect';
import { lookupLabel } from '@/lib/lookups';
import type { LeadResponse, LookupItem } from '@/types/shared';
import './LeadPanels.css';

interface LocalState {
  fullName: string;
  phone: string;
  secondaryPhone: string;
  email: string;
  gradeInterestedId: number | '';
  schoolInterestedId: number | '';
}

interface FieldErrors {
  fullName?: string;
  phone?: string;
}

function toLocal(lead: LeadResponse): LocalState {
  return {
    fullName: lead.fullName,
    phone: lead.phone,
    secondaryPhone: lead.secondaryPhone ?? '',
    email: lead.email ?? '',
    gradeInterestedId: lead.gradeInterested?.id ?? '',
    schoolInterestedId: lead.schoolInterested?.id ?? '',
  };
}

function isSameState(a: LocalState, b: LocalState): boolean {
  return (
    a.fullName === b.fullName &&
    a.phone === b.phone &&
    a.secondaryPhone === b.secondaryPhone &&
    a.email === b.email &&
    a.gradeInterestedId === b.gradeInterestedId &&
    a.schoolInterestedId === b.schoolInterestedId
  );
}

interface LeadInfoPanelProps {
  lead: LeadResponse;
  canEdit: boolean;
  grades: LookupItem[];
  schools: LookupItem[];
  onSaved: (updated: LeadResponse) => void;
}

/** Contact + interest info — the one non-status-machine editable slice of a lead, saved
 * independently via `PATCH /leads/{id}` (mirrors `ExamSectionPanel`/`InstallmentCard`'s per-panel
 * save pattern). Status, its sub-fields, notes and assignment each save through their own sibling
 * panel instead. */
export function LeadInfoPanel({ lead, canEdit, grades, schools, onSaved }: LeadInfoPanelProps) {
  const { t, i18n } = useTranslation();
  const [local, setLocal] = useState<LocalState>(() => toLocal(lead));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [panelError, setPanelError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setLocal(toLocal(lead));
  }, [lead]);

  const dirty = !isSameState(local, toLocal(lead));

  function handleChange<K extends keyof LocalState>(key: K, value: LocalState[K]) {
    setPanelError(null);
    setSavedFlash(false);
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    setLocal((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (!local.fullName.trim()) errors.fullName = t('leads.detail.errors.fullNameRequired');
    if (!local.phone.trim()) errors.phone = t('leads.detail.errors.phoneRequired');
    return errors;
  }

  async function handleSave() {
    setPanelError(null);
    setSavedFlash(false);
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const updated = await updateLead(lead.leadId, {
        fullName: local.fullName.trim(),
        phone: local.phone.trim(),
        secondaryPhone: local.secondaryPhone.trim() || null,
        email: local.email.trim() || null,
        gradeInterestedId: local.gradeInterestedId === '' ? null : local.gradeInterestedId,
        schoolInterestedId: local.schoolInterestedId === '' ? null : local.schoolInterestedId,
      });
      onSaved(updated);
      setSavedFlash(true);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      setPanelError(apiError.message || t('leads.detail.errors.infoSaveFailed'));
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setLocal(toLocal(lead));
    setFieldErrors({});
    setPanelError(null);
  }

  return (
    <section className="fa-form-section fa-lead-panel">
      <h2 className="fa-form-section__title">{t('leads.detail.infoTitle')}</h2>

      {canEdit ? (
        <>
          <div className="fa-field-row">
            <FormField
              id="lead-fullName"
              label={t('leads.detail.fullName')}
              required
              error={fieldErrors.fullName}
            >
              <input
                type="text"
                value={local.fullName}
                disabled={saving}
                onChange={(e) => handleChange('fullName', e.target.value)}
              />
            </FormField>
            <FormField
              id="lead-phone"
              label={t('leads.detail.phone')}
              required
              error={fieldErrors.phone}
            >
              <input
                type="tel"
                className="fa-ltr-isolate"
                value={local.phone}
                disabled={saving}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
            </FormField>
          </div>

          <div className="fa-field-row">
            <FormField id="lead-secondaryPhone" label={t('leads.detail.secondaryPhone')}>
              <input
                type="tel"
                className="fa-ltr-isolate"
                value={local.secondaryPhone}
                disabled={saving}
                onChange={(e) => handleChange('secondaryPhone', e.target.value)}
              />
            </FormField>
            <FormField id="lead-email" label={t('leads.detail.email')}>
              <input
                type="email"
                className="fa-ltr-isolate"
                value={local.email}
                disabled={saving}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </FormField>
          </div>

          <div className="fa-field-row">
            <FormField id="lead-gradeInterestedId" label={t('leads.detail.gradeInterested')}>
              <LookupSelect
                id="lead-gradeInterestedId"
                value={local.gradeInterestedId}
                onChange={(v) => handleChange('gradeInterestedId', v)}
                items={grades}
                placeholder={t('leads.form.gradePlaceholder')}
                disabled={saving}
              />
            </FormField>
            <FormField id="lead-schoolInterestedId" label={t('leads.detail.schoolInterested')}>
              <LookupSelect
                id="lead-schoolInterestedId"
                value={local.schoolInterestedId}
                onChange={(v) => handleChange('schoolInterestedId', v)}
                items={schools}
                placeholder={t('leads.form.schoolPlaceholder')}
                disabled={saving}
              />
            </FormField>
          </div>

          <div className="fa-lead-panel__actions">
            <button
              type="button"
              className="fa-btn fa-btn--ghost"
              disabled={!dirty || saving}
              onClick={handleReset}
            >
              {t('leads.detail.resetInfo')}
            </button>
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              {saving ? t('leads.detail.saving') : t('common.save')}
            </button>
            {savedFlash && !dirty && (
              <span className="fa-lead-panel__saved" role="status">
                {t('leads.detail.saved')}
              </span>
            )}
            {panelError && (
              <p className="fa-field__error" role="alert">
                {panelError}
              </p>
            )}
          </div>
        </>
      ) : (
        <dl className="fa-detail-list">
          <DetailRow label={t('leads.detail.fullName')} value={lead.fullName} />
          <DetailRow label={t('leads.detail.phone')} value={lead.phone} ltr />
          <DetailRow
            label={t('leads.detail.secondaryPhone')}
            value={lead.secondaryPhone ?? '—'}
            ltr
          />
          <DetailRow label={t('leads.detail.email')} value={lead.email ?? '—'} ltr />
          <DetailRow
            label={t('leads.detail.gradeInterested')}
            value={lookupLabel(lead.gradeInterested, i18n.language) || '—'}
          />
          <DetailRow
            label={t('leads.detail.schoolInterested')}
            value={lookupLabel(lead.schoolInterested, i18n.language) || '—'}
          />
        </dl>
      )}
    </section>
  );
}

function DetailRow({ label, value, ltr }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="fa-detail-row">
      <dt>{label}</dt>
      <dd className={ltr ? 'fa-ltr-isolate' : undefined}>{value}</dd>
    </div>
  );
}
